import { useChoices } from "@features/cx/paymentsv2/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useState } from "react";

interface CheckRegisterFilterProps {
  currentFilters?: { search?: string; bank?: string; source?: string };
  onApplyFilters: (filters: any) => void;
  onResetFilters: () => void;
}

export const CheckRegisterFilter: React.FC<CheckRegisterFilterProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  const [tempFilters, setTempFilters] = useState({
    bank: currentFilters?.bank || "all",
    source: currentFilters?.source || "all",
  });
  const { data: bankData } = useChoices("bank_type");
  const { data: sourceData } = useChoices("cheque_source");

  const bankOptions = React.useMemo(() => {
    const defaultOption = { label: "All banks", value: "all" };
    if (!bankData) return [defaultOption];
    return [
      defaultOption,
      ...bankData.map((bank: any) => ({
        label: bank.value,
        value: bank.key,
      }))
    ];
  }, [bankData]);

  const sourceOptions = React.useMemo(() => {
    const defaultOption = { label: "All sources", value: "all" };
    if (!sourceData) return [defaultOption];
    return [
      defaultOption,
      ...sourceData.map((source: any) => ({
        label: source.value,
        value: source.key,
      }))
    ];
  }, [sourceData]);

  const createFormFields = (): FormField[] => [
    {
      name: "bank",
      type: "select",
      label: "Bank",
      required: false,
      options: bankOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "source",
      type: "select",
      label: "Source",
      required: false,
      options: sourceOptions,
      classes: FIELD_CLASSES,
    },
  ];

  const form = FormService.createForm({
    fields: createFormFields(),
    defaultValues: tempFilters,
  });

  useEffect(() => {
    const newTempFilters = {
      bank: currentFilters?.bank || "all",
      source: currentFilters?.source || "all",
    };
    setTempFilters(newTempFilters);
    form.reset(newTempFilters);
  }, [currentFilters, form]);

  const handleSubmit = (data: any) => {
    const filtersToApply: any = {
      bank: data.bank && data.bank !== "all" ? data.bank : undefined,
      source: data.source && data.source !== "all" ? data.source : undefined,
    };
    onApplyFilters(filtersToApply);
  };

  const handleReset = () => {
    const defaultFilters = { bank: "all", source: "all" };
    setTempFilters(defaultFilters);
    form.reset(defaultFilters);
    onResetFilters();
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleReset}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={createFormFields()}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      onFieldChange={handleFieldChange}
      config={{
        showSubmitButton: false,
        showResetButton: false,
        layout: "vertical",
        spacing: "medium",
      }}
      gridColumns={2}
      responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
      className="space-y-6"
      renderCustomFooter={renderCustomFooter}
    />
  );
};
